import React, {useState} from "react";
import toast from "react-hot-toast";
import {Input} from "../../../components";
import {UserService} from "../../../services";
import {sanitizeToNumbers} from "../../../utils";
import {PasswordActive, PasswordInactive} from "../../../assets/icons";

const ModalCreateOwner = ({onClose, onSuccess, initialData}) => {
  const {form, isLoading, onChange, submit} = useForm({
    initialData,
    onClose,
    onSuccess,
  });
  const [showPassword, setShowPassword] = useState(false);
  const isEdit = !!initialData?.id;

  return (
    <div className="w-[48rem] bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4">
        <h2 className="text-lg font-bold text-text">
          {isEdit ? "Editar" : "Crear"} Propietario
        </h2>
        <button
          type="button"
          className="p-1.5 rounded-full hover:bg-gray5 transition-colors"
          onClick={onClose}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 text-text"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18 18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 px-6 mt-4">
        <Input
          label="Nombre"
          value={form.person.name || ""}
          onValueChange={(v) => onChange("person.name", v)}
        />
        <div>
          <p className="mb-1 text-text text-sm">Rif/C.I.</p>
          <div className="flex items-center border border-gray6 bg-white h-10 w-full rounded-full overflow-hidden">
            <input
              type="text"
              className="flex-1 h-full outline-none border-none bg-transparent px-4 text-sm"
              placeholder="Número de documento"
              value={form.person.document || ""}
              onChange={(e) =>
                onChange("person.document", sanitizeToNumbers(e.target.value))
              }
            />
          </div>
        </div>
        <Input
          label="Correo"
          value={form.email || ""}
          onValueChange={(v) => onChange("email", v)}
        />
        <Input
          label="Teléfono"
          value={form.person.phone || ""}
          onValueChange={(v) => onChange("person.phone", sanitizeToNumbers(v))}
          maxLength={11}
        />
        <Input
          className={{base: "col-span-2"}}
          label="Dirección"
          value={form.person?.address_house || ""}
          onValueChange={(v) => onChange("person.address_house", v)}
        />

        <div className="col-span-2 mt-2">
          <p className="text-lg font-semibold text-black">Contraseña</p>
        </div>
        <Input
          label="Contraseña"
          type={showPassword ? "text" : "password"}
          value={form.password || ""}
          onValueChange={(v) => onChange("password", v)}
          append={
            <button
              type="button"
              tabIndex="-1"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400"
            >
              <img
                src={showPassword ? PasswordActive : PasswordInactive}
                className="w-6 h-6"
                alt=""
              />
            </button>
          }
        />
        <Input
          label="Repetir contraseña"
          type={showPassword ? "text" : "password"}
          value={form.password_confirmation || ""}
          onValueChange={(v) => onChange("password_confirmation", v)}
          append={
            <button
              type="button"
              tabIndex="-1"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400"
            >
              <img
                src={showPassword ? PasswordActive : PasswordInactive}
                className="w-6 h-6"
                alt=""
              />
            </button>
          }
        />
      </div>

      <div className="flex justify-center mt-6 px-6 py-4 gap-3">
        <button
          type="button"
          className="px-6 py-2 bg-gray-200 text-text font-semibold rounded-full hover:bg-gray-300 transition-colors text-sm"
          onClick={onClose}
          disabled={isLoading}
        >
          Cerrar
        </button>
        <button
          type="button"
          className="px-6 py-2 bg-red text-white font-semibold rounded-full hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
          onClick={submit}
          disabled={isLoading}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  );
};

const useForm = ({initialData, onClose, onSuccess}) => {
  const isEdit = !!initialData?.id;

  const getInitial = (data) => ({
    id: data?.id || "",
    email: data?.email || "",
    password: "",
    password_confirmation: "",
    person: {
      name: data?.person?.name || "",
      phone: data?.person?.phone || "",
      document: data?.person?.document || "",
      address_house: data?.person?.address_house || "",
    },
  });

  const [form, setForm] = useState(() => getInitial(initialData));
  const [isLoading, setIsLoading] = useState(false);

  const onChange = (target, value) => {
    if (target.startsWith("person.")) {
      const key = target.split(".")[1];
      setForm((s) => ({...s, person: {...s.person, [key]: value}}));
    } else {
      setForm((s) => ({...s, [target]: value}));
    }
  };

  const isValidForm = () => {
    const onError = (msg) => {
      toast.error(msg);
      return false;
    };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.person.name.trim()) return onError("El nombre es requerido");
    if (!form.person.document.trim())
      return onError("El número de C.I. es requerido");
    if (form.person.document.length < 5)
      return onError("El número de C.I. debe tener al menos 5 caracteres");
    if (!form.email.trim())
      return onError("El correo electrónico es requerido");
    if (!emailRegex.test(form.email))
      return onError("El correo electrónico no es válido");
    if (!form.person.phone.trim()) return onError("El teléfono es requerido");
    if (form.person.phone.length < 7)
      return onError("El teléfono debe tener al menos 7 dígitos");
    if (!form.person.address_house.trim())
      return onError("La dirección es requerida");

    if (!isEdit && !form.password) return onError("La contraseña es requerida");
    if (form.password && form.password.length < 6)
      return onError("La contraseña debe tener al menos 6 caracteres");
    if (form.password !== form.password_confirmation)
      return onError("Las contraseñas no coinciden");

    return true;
  };

  const submit = async () => {
    if (!isValidForm()) return;
    setIsLoading(true);
    try {
      const payload = {
        ...form,
        person: JSON.stringify(form.person),
      };

      if (isEdit) {
        await UserService.admin.updateOwner(payload);
        toast.success("Propietario actualizado con éxito");
      } else {
        await UserService.admin.createOwner(payload);
        toast.success("Propietario creado con éxito");
      }
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {form, isLoading, onChange, submit};
};

export default ModalCreateOwner;
